import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Camera, Loader2, Check, X, Megaphone, AlertTriangle, MapPin, Calendar, Users, Trash2, Package, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ChatParticipants from './ChatParticipants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Toronto' }) : '';
const mediaUrl = (id) => `${API}/dispatch-chat/media/${id}?token=${encodeURIComponent(localStorage.getItem('token') || '')}`;

const resizePhoto = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    resolve(c.toDataURL('image/jpeg', 0.8));
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('bad image')); };
  img.src = url;
});

const StatusChip = ({ s }) => {
  const st = s.status;
  const cls = st === 'confirmed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : st === 'declined' ? 'bg-red-50 text-red-700 border-red-200'
    : st === 'notified' ? 'bg-slate-200 text-slate-700 border-slate-300'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  const icon = st === 'confirmed' ? '✓' : st === 'declined' ? '✗' : st === 'notified' ? '·' : '⏳';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${cls}`} data-testid={`chat-status-${s.crew_member_id}`}>
      {icon} {s.crew_member_name?.split(' ')[0] || 'Crew'}{s.role && s.role !== 'crew' ? <span className="opacity-60 uppercase text-[9px]">{s.role}</span> : null}
    </span>
  );
};

const DeleteBtn = ({ m, onDelete, light }) => (
  <button onClick={() => onDelete(m)} title="Delete message" className={`opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded ${light ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-900/10' : 'text-slate-500 hover:text-red-700 hover:bg-red-50'}`} data-testid={`chat-msg-delete-${m.id}`}>
    <Trash2 className="w-3.5 h-3.5" />
  </button>
);

const Message = ({ m, mine, canDelete, onDelete }) => {
  if (m.kind === 'reply') {
    const yes = m.reply_value === 'Y';
    return (
      <div className="flex justify-center my-1" data-testid={`chat-msg-${m.id}`}>
        <span className={`text-xs px-3 py-1 rounded-full border ${yes ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
          {yes ? '✅' : '❌'} <b>{m.sender_name}</b> {yes ? 'confirmed' : "can't make it"} · {fmtTime(m.created_at)}
        </span>
      </div>
    );
  }
  if (m.kind === 'crib') {
    return (
      <div className="group rounded-xl border p-3 my-2 bg-emerald-50 border-emerald-300" data-testid={`chat-msg-${m.id}`}>
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide mb-1.5 text-emerald-700">
          <Wrench className="w-3.5 h-3.5" />
          <span className="flex-1">Tool crib heads-up · {m.sender_name} · {fmtTime(m.created_at)}</span>
          {canDelete && <DeleteBtn m={m} onDelete={onDelete} />}
        </div>
        <pre className="text-[13px] text-slate-800 whitespace-pre-wrap font-sans leading-relaxed">{m.body}</pre>
        {m.kit_id && (
          <a href={`/tool-crib?kit=${m.kit_id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 bg-white border border-emerald-300 rounded-lg px-2.5 py-1 hover:bg-emerald-100" data-testid={`chat-msg-kit-${m.id}`}>
            <Wrench className="w-3.5 h-3.5" /> Open box {m.kit_number || ''}
          </a>
        )}
      </div>
    );
  }
  if (m.kind === 'dispatch' || m.kind === 'notice') {
    const isNotice = m.kind === 'notice';
    return (
      <div className={`group rounded-xl border p-3 my-2 ${isNotice ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-300'}`} data-testid={`chat-msg-${m.id}`}>
        <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide mb-1.5 ${isNotice ? 'text-amber-700' : 'text-blue-700'}`}>
          {isNotice ? <AlertTriangle className="w-3.5 h-3.5" /> : <Megaphone className="w-3.5 h-3.5" />}
          <span className="flex-1">{isNotice ? 'Job update' : 'Dispatch'} · {m.sender_name} · {fmtTime(m.created_at)}</span>
          {canDelete && <DeleteBtn m={m} onDelete={onDelete} />}
        </div>
        <pre className="text-[13px] text-slate-800 whitespace-pre-wrap font-sans leading-relaxed">{m.body}</pre>
        {(m.media || []).length > 0 && <Media ids={m.media} />}
      </div>
    );
  }
  return (
    <div className={`group flex items-end gap-1 ${mine ? 'justify-end' : 'justify-start'} my-1`} data-testid={`chat-msg-${m.id}`}>
      {mine && canDelete && <DeleteBtn m={m} onDelete={onDelete} />}
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${mine ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-800 rounded-bl-sm'}`}>
        {!mine && <p className="text-[11px] font-semibold text-blue-800 mb-0.5">{m.sender_name}</p>}
        {(m.media || []).length > 0 && <Media ids={m.media} />}
        {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
        <p className={`text-[10px] mt-0.5 ${mine ? 'text-blue-800' : 'text-slate-500'}`}>{fmtTime(m.created_at)}</p>
      </div>
      {!mine && canDelete && <DeleteBtn m={m} onDelete={onDelete} />}
    </div>
  );
};

const Media = ({ ids }) => (
  <div className="flex flex-wrap gap-1.5 mb-1">
    {ids.map((id) => (
      <a key={id} href={mediaUrl(id)} target="_blank" rel="noreferrer">
        <img src={mediaUrl(id)} alt="attachment" className="w-24 h-24 object-cover rounded-lg border border-slate-200" />
      </a>
    ))}
  </div>
);

// Shared job-chat thread: header, crew ✓/✗ statuses, messages, reply bar, composer.
// Used full-page (/dispatch-chat) and embedded in the Schedule work-period modal.
export default function DispatchThreadPanel({ threadId, currentUser, embedded = false, onStatusChange }) {
  const [thread, setThread] = useState(null);
  const [error, setError] = useState(null);
  const [text, setText] = useState('');
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [replying, setReplying] = useState(false);
  const [changeAnswer, setChangeAnswer] = useState(false);
  const [showPeople, setShowPeople] = useState(false);
  const [jobBox, setJobBox] = useState(null); // open tool-crib box for this WO (foreman can view / add)
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const lastCount = useRef(0);

  const load = useCallback(async (silent = false) => {
    if (!threadId) return;
    try {
      const r = await axios.get(`${API}/dispatch-chat/threads/${threadId}`, auth());
      setThread(r.data);
      if (!silent && r.data.work_order_number) {
        axios.get(`${API}/tool-crib/kits/for-wo/${encodeURIComponent(r.data.work_order_number)}`, auth())
          .then((k) => {
            const open = (k.data.kits || []).filter((x) => x.status !== 'closed');
            setJobBox(open.find((x) => r.data.request_id && x.dispatch_request_id === r.data.request_id) || open[0] || null);
          })
          .catch(() => setJobBox(null)); // 403 for crew without crib access → no button
      }
      setError(null);
      if ((r.data.messages || []).length !== lastCount.current) {
        lastCount.current = (r.data.messages || []).length;
        axios.post(`${API}/dispatch-chat/threads/${threadId}/read`, {}, auth()).catch(() => {});
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: silent ? 'smooth' : 'auto', block: 'end' }), 50);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load this chat');
    }
  }, [threadId]);

  useEffect(() => { lastCount.current = 0; setThread(null); load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), 8000);
    return () => clearInterval(t);
  }, [load]);

  const reply = async (value) => {
    setReplying(true);
    try {
      await axios.post(`${API}/dispatch-chat/threads/${threadId}/reply`, { value }, auth());
      toast.success(value === 'Y' ? "You're confirmed ✓" : 'Marked as unavailable — dispatch has been notified');
      setChangeAnswer(false);
      await load(true);
      onStatusChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send your reply'); }
    setReplying(false);
  };

  const deleteMessage = async (m) => {
    if (!window.confirm('Delete this message for everyone?')) return;
    try {
      await axios.delete(`${API}/dispatch-chat/threads/${threadId}/messages/${m.id}`, auth());
      lastCount.current = 0;
      await load(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  const addPhotos = async (files) => {
    const imgs = Array.from(files || []).filter((f) => f.type.startsWith('image/'));
    for (const f of imgs.slice(0, 5 - photos.length)) {
      try { const d = await resizePhoto(f); setPhotos((p) => p.length >= 5 ? p : [...p, d]); } catch { toast.error(`Couldn't read ${f.name}`); }
    }
  };

  const send = async () => {
    if ((!text.trim() && !photos.length) || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/dispatch-chat/threads/${threadId}/messages`, { body: text.trim(), photos: photos.map((d) => ({ data: d })) }, auth());
      setText(''); setPhotos([]);
      await load(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send'); }
    setSending(false);
  };

  if (error) return <div className="p-4 text-sm text-red-700" data-testid="chat-error">{error}</div>;
  if (!thread) return <div className="p-6 flex items-center justify-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading chat…</div>;

  const crewStatuses = (thread.statuses || []).filter((s) => (s.role || 'crew') === 'crew');
  const fyiStatuses = (thread.statuses || []).filter((s) => (s.role || 'crew') !== 'crew');
  const confirmed = crewStatuses.filter((s) => s.status === 'confirmed').length;
  const cancelled = thread.status === 'cancelled';
  const myId = currentUser?.id;
  const showReplyBar = thread.can_reply && !cancelled && (thread.my_status === 'sent' || changeAnswer);
  const isMgmt = !!thread.is_mgmt;

  return (
    <div className={`relative flex flex-col ${embedded ? 'h-[520px]' : 'h-full'} bg-white ${embedded ? 'rounded-xl border border-slate-200' : ''}`} data-testid="dispatch-thread-panel">
      {showPeople && <ChatParticipants thread={thread} isMgmt={isMgmt} myId={myId} onClose={() => setShowPeople(false)} onChanged={() => load(true)} />}
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-slate-900 font-bold text-base truncate" data-testid="chat-thread-title">{thread.title}{cancelled && <span className="ml-2 text-xs text-red-700 font-semibold uppercase">cancelled</span>}</h3>
            <p className="text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
              {thread.date_label && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{thread.date_label}</span>}
              {thread.job_site && <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{thread.job_site}</span>}
              <button onClick={() => setShowPeople(true)} className="inline-flex items-center gap-1 hover:text-slate-900 underline-offset-2 hover:underline" title="See who's in this chat" data-testid="chat-participants-btn"><Users className="w-3 h-3" />{(thread.participants || []).length} in chat</button>
              {jobBox && (
                <button onClick={() => navigate(`/tool-crib?kit=${jobBox.id}`)} className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 underline-offset-2 hover:underline" title="See what's in the job box — or add to it" data-testid="chat-job-box-btn">
                  <Package className="w-3 h-3" />Job box · {jobBox.items?.length ?? 0} item{(jobBox.items?.length ?? 0) === 1 ? '' : 's'}{jobBox.status === 'to_pack' ? ' · to pack' : ' · on site'}
                </button>
              )}
            </p>
          </div>
          {crewStatuses.length > 0 && (
            <span className={`flex-shrink-0 text-xs font-bold px-2 py-1 rounded-lg ${confirmed === crewStatuses.length ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-testid="chat-confirm-count">
              {confirmed}/{crewStatuses.length} confirmed
            </span>
          )}
        </div>
        {(crewStatuses.length > 0 || fyiStatuses.length > 0) && (
          <div className="flex flex-wrap gap-1 mt-2" data-testid="chat-status-strip">
            {crewStatuses.map((s) => <StatusChip key={s.crew_member_id} s={s} />)}
            {fyiStatuses.map((s) => <StatusChip key={s.crew_member_id} s={s} />)}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="chat-messages">
        {(thread.messages || []).length === 0 && (
          <p className="text-center text-xs text-slate-500 py-8">No messages yet — the dispatch will appear here when it's posted.</p>
        )}
        {(thread.messages || []).map((m) => <Message key={m.id} m={m} mine={m.sender_id === myId} canDelete={isMgmt || m.sender_id === myId} onDelete={deleteMessage} />)}
        <div ref={bottomRef} />
      </div>

      {/* ✓ / ✗ reply bar */}
      {showReplyBar && (
        <div className="px-4 py-3 border-t border-blue-200 bg-blue-50" data-testid="chat-reply-bar">
          <p className="text-xs text-blue-800 font-semibold mb-2">Can you make this job?</p>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => reply('Y')} disabled={replying}
              className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white font-bold py-3 rounded-xl text-sm"
              data-testid="chat-reply-yes"><Check className="w-5 h-5" /> Confirm — I'm in</button>
            <button onClick={() => reply('N')} disabled={replying}
              className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-bold py-3 rounded-xl text-sm"
              data-testid="chat-reply-no"><X className="w-5 h-5" /> Can't make it</button>
          </div>
        </div>
      )}
      {thread.can_reply && !cancelled && !showReplyBar && (thread.my_status === 'confirmed' || thread.my_status === 'declined') && (
        <div className="px-4 py-2 border-t border-slate-200 flex items-center justify-between text-xs" data-testid="chat-my-answer">
          <span className={thread.my_status === 'confirmed' ? 'text-emerald-700' : 'text-red-700'}>
            {thread.my_status === 'confirmed' ? '✅ You confirmed this job' : "❌ You said you can't make it"}
          </span>
          <button onClick={() => setChangeAnswer(true)} className="text-slate-500 hover:text-slate-900 underline" data-testid="chat-change-answer">Change answer</button>
        </div>
      )}

      {/* Composer */}
      {!cancelled && (
        <div className="px-3 py-2.5 border-t border-slate-200 bg-white" data-testid="chat-composer">
          {photos.length > 0 && (
            <div className="flex gap-2 mb-2">
              {photos.map((d, i) => (
                <div key={i} className="relative">
                  <img src={d} alt="" className="w-12 h-12 object-cover rounded-lg border border-slate-300" />
                  <button onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-4 h-4 text-[10px] leading-4" data-testid={`chat-photo-remove-${i}`}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <button onClick={() => fileRef.current?.click()} className="p-2.5 rounded-xl bg-white hover:bg-slate-100 text-slate-700" title="Attach photo" data-testid="chat-attach-btn">
              <Camera className="w-5 h-5" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="chat-photo-input"
              onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }} />
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} maxLength={2000}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !('ontouchstart' in window)) { e.preventDefault(); send(); } }}
              placeholder="Message the crew…"
              className="flex-1 resize-none bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 max-h-28"
              data-testid="chat-input" />
            <button onClick={send} disabled={sending || (!text.trim() && !photos.length)}
              className="p-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white" data-testid="chat-send-btn">
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
